import { useState, type CSSProperties } from "react";
import { Pencil, Plus, X, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { FIELD_CLASS } from "./log-controls.utils";

/**
 * Presentational row helpers shared across the Workshop Log cluster
 * (DecisionLogModal + the register sections + the Repository's transcript
 * list). Only the pieces that are genuinely identical live here — the
 * `ROW_CARD`/`ROW_TITLE`/`ROW_SUB` styles intentionally differ between the
 * editor rows and the read-through cards, so they stay co-located with
 * their owners.
 */

/** Empty-state line for a log/list section. Renders as an `<li>` so it
 *  drops straight into the `<ul>` lists these sections use. */
export function EmptyHint({ text }: { text: string }) {
  return (
    <li
      style={{
        fontSize: "var(--fs-label)",
        color: "var(--text-muted)",
        letterSpacing: "0.5px",
        padding: "4px 2px",
      }}
    >
      {text}
    </li>
  );
}

const REMOVE_BUTTON_STYLE: CSSProperties = {
  flex: "none",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 24,
  height: 24,
  borderRadius: 6,
  border: 0,
  background: "transparent",
  color: "var(--text-muted)",
  cursor: "pointer",
};

/** Ghost "remove" (✕) button — neutral by default, reddens on hover.
 *  `style` merges over the base for per-row tweaks (e.g. the transcript
 *  row aligns it to the top of an expandable card). */
export function RemoveButton({
  label,
  onClick,
  style,
}: {
  label: string;
  onClick: () => void;
  style?: CSSProperties;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title="Remove"
      style={{ ...REMOVE_BUTTON_STYLE, ...style }}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.color = "var(--accent-red)";
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.color = "var(--text-muted)";
      }}
    >
      <X aria-hidden style={{ width: 13, height: 13 }} strokeWidth={2.5} />
    </button>
  );
}

/** Click-to-edit row title. Enter or blur saves, Escape cancels; a blank or
 *  unchanged value saves nothing. The input carries `data-inline-edit` so
 *  the host dialog can keep Escape from closing the modal mid-edit. */
export function EditableTitle({
  value,
  label,
  onSave,
  style,
}: {
  value: string;
  /** Field name, e.g. "decision title". Used for the a11y labels. */
  label: string;
  onSave: (next: string) => void;
  style?: CSSProperties;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  function commit(): void {
    const next = draft.trim();
    if (next && next !== value) onSave(next);
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        type="text"
        data-inline-edit=""
        aria-label={`Edit ${label}`}
        value={draft}
        autoFocus
        onFocus={(e) => e.currentTarget.select()}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") setEditing(false);
        }}
        className={cn(FIELD_CLASS, "py-1 font-semibold")}
      />
    );
  }

  return (
    <button
      type="button"
      aria-label={`Edit ${label}: ${value}`}
      title="Edit"
      onClick={() => {
        setDraft(value);
        setEditing(true);
      }}
      className="group inline-flex max-w-full cursor-pointer items-center gap-1.5 rounded-sm text-left focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
      style={{ ...style, background: "transparent", border: 0, padding: 0 }}
    >
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{value}</span>
      <Pencil
        aria-hidden
        className="flex-none opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100"
        style={{ width: 12, height: 12, color: "var(--text-muted)" }}
      />
    </button>
  );
}

/** Uppercase section heading with an icon — the label above each register
 *  block in the Workshop Log. */
export function SectionLabel({
  icon: Icon,
  text,
}: {
  icon: LucideIcon;
  text: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: "var(--fs-label)",
        letterSpacing: "1.5px",
        textTransform: "uppercase",
        color: "var(--text-secondary)",
        fontWeight: 700,
      }}
    >
      <Icon aria-hidden style={{ width: 14, height: 14, color: "var(--accent-true-blue)" }} />
      {text}
    </div>
  );
}

/** The "+ ADD" button that submits a register's new-entry form. */
export function AddButton({
  onClick,
  disabled,
  label = "ADD",
}: {
  onClick: () => void;
  disabled: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="modal-close-btn inline-flex items-center gap-1.5"
      style={{ flex: "none", opacity: disabled ? 0.5 : 1, cursor: disabled ? "default" : "pointer" }}
    >
      <Plus aria-hidden style={{ width: 12, height: 12 }} strokeWidth={2.5} />
      {label}
    </button>
  );
}
